import {assert} from 'Asserts';

import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
} from '../../processFlowchartConfigEditorConstants';
import {
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {
  buildTransitionIndexByPTDVPair,
  createEmptyTransition,
  getFieldPairKey,
  getOrCreateTransition,
  getPTDVPairKey,
} from './transitionKeyUtils';
import {Transition} from './TransitionTypes';

export interface CreateTransitionResult {
  transition: Transition;
  /** Whether a new transition was created */
  isNew: boolean;
}
/**
 * Resolves the source and target PTDV IDs for an edge.
 * When a node has been deleted, its PTDV ID can no longer be found in nodesStaticData.
 * In that case, deletedNodeContext provides the PTDV IDs captured before removal.
 */
export function getPTDVIdsFromEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  deletedNodeContext?: {deletedSourceNodePTDVId?: string; deletedTargetNodePTDVId?: string}
): {sourcePTDVId: string; targetPTDVId: string} | null {
  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);

  const sourcePTDVId =
    sourceNode && isStaticTaskSchemaNodeData(sourceNode)
      ? sourceNode.taskDefinitionVersionSpec.clientId
      : deletedNodeContext?.deletedSourceNodePTDVId;

  const targetPTDVId =
    targetNode && isStaticTaskSchemaNodeData(targetNode)
      ? targetNode.taskDefinitionVersionSpec.clientId
      : deletedNodeContext?.deletedTargetNodePTDVId;

  if (sourcePTDVId == null || targetPTDVId == null) {
    return null;
  }

  return {sourcePTDVId, targetPTDVId};
}

function getFieldPairKeyFromDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  deletedNodeContext?: {deletedSourceNodePTDVId?: string; deletedTargetNodePTDVId?: string}
): string | null {
  if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  const ptdvIds = getPTDVIdsFromEdge(edge, nodes, deletedNodeContext);
  if (!ptdvIds) {
    return null;
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  return getFieldPairKey({
    sourcePTDVId: ptdvIds.sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId: ptdvIds.targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });
}

export function getOrCreateTransitionForEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transitions: Record<string, Transition>
): CreateTransitionResult | null {
  if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return null;
  }

  const ptdvIds = getPTDVIdsFromEdge(edge, nodes);
  if (!ptdvIds) {
    return null;
  }

  const {sourcePTDVId, targetPTDVId} = ptdvIds;
  const index = buildTransitionIndexByPTDVPair(transitions);
  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const existingTransitionId = index[pairKey]?.[0];

  if (existingTransitionId != null && transitions[existingTransitionId]) {
    return {
      transition: transitions[existingTransitionId],
      isNew: false,
    };
  }

  const newTransition = createEmptyTransition({
    sourcePTDVIds: [sourcePTDVId],
    targetPTDVId,
  });
  return {
    transition: newTransition,
    isNew: true,
  };
}

/**
 * Returns the transition ID that should be deleted after a control flow edge was removed,
 * or null if the transition should be kept.
 *
 * When a node has been deleted, its PTDV ID can no longer be resolved from nodesStaticData.
 * In that case, the caller passes deletedNodeContext with the PTDV IDs captured before the
 * node was removed, so we can still identify the affected transition.
 *
 * TODO(BNCH-139872): This should handle merging transitions better
 */
export function findOrphanedTransitionId(
  removedEdge: FlowchartStaticEdgeData,
  remainingEdges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  deletedNodeContext?: {deletedSourceNodePTDVId?: string; deletedTargetNodePTDVId?: string}
): string | null {
  if (removedEdge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return null;
  }

  const ptdvIds = getPTDVIdsFromEdge(removedEdge, nodes, deletedNodeContext);
  if (!ptdvIds) {
    return null;
  }

  const {sourcePTDVId, targetPTDVId} = ptdvIds;
  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const transitionIds = ptdvPairToTransitionIdIndex[pairKey];
  const transitionId = transitionIds?.[0];

  if (!transitionId) {
    return null;
  }

  const nodeToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }
  if (deletedNodeContext?.deletedSourceNodePTDVId != null) {
    nodeToPTDVId.set(removedEdge.source, deletedNodeContext.deletedSourceNodePTDVId);
  }
  if (deletedNodeContext?.deletedTargetNodePTDVId != null) {
    nodeToPTDVId.set(removedEdge.target, deletedNodeContext.deletedTargetNodePTDVId);
  }

  // TODO(BNCH-139872): Check all source PTDVs for the transition, not just
  // the source PTDV related to the removed edge
  const hasRemainingControlFlowEdgeBetweenPTDVs = remainingEdges.some((edge) => {
    if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
      return false;
    }
    return nodeToPTDVId.get(edge.source) === sourcePTDVId && nodeToPTDVId.get(edge.target) === targetPTDVId;
  });

  return hasRemainingControlFlowEdgeBetweenPTDVs || transitionIds.length > 1 ? null : transitionId;
}

/**
 * Find all transitions affected by changes to nodes or edges.
 * Useful for determining which transitions need updates when the flowchart changes.
 */
export function getAffectedTransitions(
  changedNodeIds: Array<string>,
  nodes: Array<StaticFlowchartNodeData>,
  transitions: Record<string, Transition>
): Array<string> {
  if (changedNodeIds.length === 0) {
    return [];
  }

  const changedPTDVIds = new Set<string>();
  for (const nodeId of changedNodeIds) {
    const node = nodes.find((n) => n.id === nodeId);
    if (node && isStaticTaskSchemaNodeData(node)) {
      changedPTDVIds.add(node.taskDefinitionVersionSpec.clientId);
    }
  }

  if (changedPTDVIds.size === 0) {
    return [];
  }

  const affectedTransitionIds: Array<string> = [];
  for (const transition of Object.values(transitions)) {
    const involvesPTDV =
      transition.sourcePTDVIds.some((id) => changedPTDVIds.has(id)) ||
      changedPTDVIds.has(transition.targetPTDVId);

    if (involvesPTDV) {
      affectedTransitionIds.push(transition.id);
    }
  }

  return affectedTransitionIds;
}

export function addCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition,
  customMappingData: CustomMappingData | null = null
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  return {
    ...transition.customMappingDataByFieldPair,
    [fieldPairKey]: customMappingData,
  };
}

/**
 * Clears the custom mapping for a data flow edge
 */
export function removeCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  return {
    ...transition.customMappingDataByFieldPair,
    [fieldPairKey]: null,
  };
}

/**
 * Removes a field pair entirely from the transition when a data flow edge is deleted.
 */
export function removeFieldPairForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition,
  deletedNodeContext?: {deletedSourceNodePTDVId?: string; deletedTargetNodePTDVId?: string}
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes, deletedNodeContext);
  if (!fieldPairKey) {
    return null;
  }

  const {[fieldPairKey]: _, ...remainingMappings} = transition.customMappingDataByFieldPair;
  return remainingMappings;
}

/**
 * Creates transitions for a newly added control flow edge.
 * Handles three cases:
 * 1. Direct task-to-task edge: Creates a single transition
 * 2. Router-to-task edge: Creates transitions from all upstream task nodes to the target
 * 3. Task-to-router edge: Creates transitions from the source to all downstream task nodes
 *
 * @returns Array of new transitions to be added (excludes transitions that already exist)
 */
export function createTransitionsForNewControlFlowEdge(
  newEdge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>,
  existingTransitions: Record<string, Transition>
): Array<Transition> {
  if (newEdge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return [];
  }

  const sourceNode = nodes.find((n) => n.id === newEdge.source);
  const targetNode = nodes.find((n) => n.id === newEdge.target);

  if (sourceNode == null || targetNode == null) {
    return [];
  }

  const newTransitions: Array<Transition> = [];
  const workingTransitions = {...existingTransitions};

  const addTransitionIfNew = (sourcePTDVId: string, targetPTDVId: string): void => {
    const {transition, isNew} = getOrCreateTransition({
      sourcePTDVId,
      targetPTDVId,
      transitions: workingTransitions,
      isPlanned: true,
    });
    if (isNew) {
      for (const edge of allEdges) {
        if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
          continue;
        }
        const ptdvIds = getPTDVIdsFromEdge(edge, nodes);
        if (ptdvIds?.sourcePTDVId === sourcePTDVId && ptdvIds.targetPTDVId === targetPTDVId) {
          const updatedMappings = addCustomMappingForDataFlowEdge(edge, nodes, transition);
          if (updatedMappings) {
            transition.customMappingDataByFieldPair = updatedMappings;
          }
        }
      }
      newTransitions.push(transition);
    }
  };

  // Case 1: Direct task-to-task edge
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    addTransitionIfNew(
      sourceNode.taskDefinitionVersionSpec.clientId,
      targetNode.taskDefinitionVersionSpec.clientId
    );
  }

  // Case 2: Router-to-task edge - create transitions from all upstream task nodes
  if (isStaticRouterNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
    const incomingEdges = allEdges.filter(
      (e) => e.target === sourceNode.id && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    assert(incomingEdges.length <= 1, 'Expected at most one incoming edge');
    const upstreamNode = nodes.find((n) => n.id === incomingEdges[0]?.source);
    if (upstreamNode != null && isStaticTaskSchemaNodeData(upstreamNode)) {
      addTransitionIfNew(upstreamNode.taskDefinitionVersionSpec.clientId, targetPTDVId);
    }
  }

  // Case 3: Task-to-router edge - create transitions to all downstream task nodes
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticRouterNodeData(targetNode)) {
    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const outgoingEdges = allEdges.filter(
      (e) => e.source === targetNode.id && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    for (const outgoingEdge of outgoingEdges) {
      const downstreamNode = nodes.find((n) => n.id === outgoingEdge.target);
      if (downstreamNode != null && isStaticTaskSchemaNodeData(downstreamNode)) {
        addTransitionIfNew(sourcePTDVId, downstreamNode.taskDefinitionVersionSpec.clientId);
      }
    }
  }

  return newTransitions;
}
